import { createApp } from "vue"
import SiteNavigation from "./components/SiteNavigation.vue"
import AiTranscriptAssistant from "./components/AiTranscriptAssistant.vue"
import AiProviderSettings from "./components/AiProviderSettings.vue"
import { initializeDateTimeRanges } from "./date-time-ranges.js"
import "../frontend/styles/application.css"

function mountNavigation() {
  const element = document.querySelector("#site-navigation")
  if (!element || element.dataset.vueMounted === "true") return

  const props = JSON.parse(element.dataset.navigation || "{}")
  createApp(SiteNavigation, props).mount(element)
  element.dataset.vueMounted = "true"
}

function mountVueIslands() {
  document.querySelectorAll("[data-ai-transcript-assistant]").forEach((element) => {
    if (element.dataset.vueMounted === "true") return
    createApp(AiTranscriptAssistant, JSON.parse(element.dataset.props || "{}")).mount(element)
    element.dataset.vueMounted = "true"
  })

  document.querySelectorAll("[data-ai-provider-settings]").forEach((element) => {
    if (element.dataset.vueMounted === "true") return
    createApp(AiProviderSettings, JSON.parse(element.dataset.props || "{}")).mount(element)
    element.dataset.vueMounted = "true"
  })
}

function initializeDialogs() {
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest('[data-bs-toggle="modal"], [data-modal-target]')
    if (trigger) {
      event.preventDefault()
      const selector = trigger.dataset.bsTarget || trigger.dataset.modalTarget
      openDialog(document.querySelector(selector))
      return
    }

    const closeButton = event.target.closest('[data-bs-dismiss="modal"], [data-bs-dismiss="alert"], [data-dismiss="alert"]')
    if (!closeButton) return

    event.preventDefault()
    const dialog = closeButton.closest(".modal")
    if (dialog) closeDialog(dialog)
    else closeButton.closest(".alert")?.remove()
  })

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      document.querySelectorAll(".modal.is-open").forEach(closeDialog)
    }
  })
}

function openDialog(dialog) {
  if (!dialog) return
  dialog.classList.add("is-open")
  dialog.removeAttribute("aria-hidden")
  document.body.classList.add("dialog-open")
  dialog.querySelector("button, input, select, textarea, a")?.focus()
}

function closeDialog(dialog) {
  dialog.classList.remove("is-open")
  dialog.setAttribute("aria-hidden", "true")
  if (!document.querySelector(".modal.is-open")) document.body.classList.remove("dialog-open")
}

function initializeLegacyUtilities() {
  document.querySelectorAll('[data-bs-toggle="tooltip"], [data-bs-toggle="popover"]').forEach((element) => {
    if (!element.title) element.title = element.dataset.bsTitle || element.dataset.bsContent || ""
  })

  document.querySelectorAll("#print").forEach((button) => {
    button.addEventListener("click", () => window.print())
  })
}

function initializeTabs() {
  document.querySelectorAll('[data-bs-toggle="tab"]').forEach((tab) => {
    if (tab.dataset.tabsInitialized === "true") return
    tab.dataset.tabsInitialized = "true"
    tab.addEventListener("click", (event) => {
      event.preventDefault()
      const target = document.querySelector(tab.getAttribute("href"))
      if (!target) return

      const tabList = tab.closest(".nav")
      tabList?.querySelectorAll('[data-bs-toggle="tab"]').forEach((item) => item.classList.remove("active"))
      target.parentElement?.querySelectorAll(".tab-pane").forEach((pane) => pane.classList.remove("active"))
      tab.classList.add("active")
      target.classList.add("active")
    })
  })
}

function initializeResponsiveTables() {
  document.querySelectorAll("table.table, table.plantable").forEach((table) => {
    if (table.parentElement?.classList.contains("table-scroll")) return

    const wrapper = document.createElement("div")
    wrapper.className = "table-scroll"
    table.parentNode.insertBefore(wrapper, table)
    wrapper.appendChild(table)
  })

  document.querySelectorAll("[data-iep-document] table.plantable").forEach((table) => {
    table.classList.add("legacy-iep-table")
    table.querySelectorAll("th[width], td[width]").forEach((cell) => cell.removeAttribute("width"))
    table.querySelectorAll("th, td").forEach((cell) => {
      const textLength = cell.textContent.trim().length
      const hasLongEditor = Boolean(cell.querySelector("textarea"))
      const hasEditor = Boolean(cell.querySelector("input, select"))
      if (hasLongEditor || textLength > 70) cell.classList.add("is-prose")
      else if (hasEditor) cell.classList.add("is-control")
      else if (cell.tagName === "TH" && textLength <= 12 && !cell.hasAttribute("colspan")) cell.classList.add("is-compact")
    })
  })
}

function initializeIepNavigation() {
  document.querySelectorAll("[data-iep-shell]").forEach((shell) => {
    if (shell.dataset.navigationInitialized === "true") return

    const documentBody = shell.querySelector("[data-iep-document]")
    const drawer = shell.querySelector("[data-iep-navigation]")
    const navigation = drawer?.querySelector("nav")
    const toggle = shell.querySelector("[data-iep-navigation-toggle]")
    const close = shell.querySelector("[data-iep-navigation-close]")
    const backdrop = shell.querySelector("[data-iep-navigation-backdrop]")
    if (!documentBody || !drawer || !navigation || !toggle || !close || !backdrop) return

    const setNavigationOpen = (open, { restoreFocus = false } = {}) => {
      shell.classList.toggle("is-navigation-open", open)
      drawer.classList.toggle("is-open", open)
      drawer.setAttribute("aria-hidden", String(!open))
      toggle.setAttribute("aria-expanded", String(open))

      if (open) close.focus()
      else if (restoreFocus) toggle.focus()
    }

    toggle.addEventListener("click", () => setNavigationOpen(true))
    close.addEventListener("click", () => setNavigationOpen(false, { restoreFocus: true }))
    backdrop.addEventListener("click", () => setNavigationOpen(false, { restoreFocus: true }))
    shell.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !drawer.classList.contains("is-open")) return
      event.preventDefault()
      setNavigationOpen(false, { restoreFocus: true })
    })

    const headings = [...documentBody.querySelectorAll("h3, h4")].filter((heading) => (
      heading.tagName === "H3" || heading.querySelector(":scope > a[id], :scope > a[name]")
    ))
    const links = headings.map((heading, index) => {
      const legacyAnchor = heading.querySelector(":scope > a[id], :scope > a[name]")
      const id = heading.id || legacyAnchor?.id || legacyAnchor?.getAttribute("name") || `iep-section-${index + 1}`
      heading.id = id
      heading.classList.add("legacy-iep-heading")

      const link = document.createElement("a")
      link.href = `#${id}`
      link.textContent = heading.textContent.trim()
      link.className = heading.tagName === "H4" ? "is-child" : "is-parent"
      return link
    })

    navigation.replaceChildren(...links)
    navigation.addEventListener("click", (event) => {
      if (event.target.closest("a")) setNavigationOpen(false)
    })
    shell.dataset.navigationInitialized = "true"

    if ("IntersectionObserver" in window) {
      const linkById = new Map(links.map((link) => [decodeURIComponent(link.hash.slice(1)), link]))
      const observer = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        if (!visible) return
        links.forEach((link) => link.removeAttribute("aria-current"))
        linkById.get(visible.target.id)?.setAttribute("aria-current", "location")
      }, { rootMargin: "-20% 0px -65%", threshold: 0 })
      headings.forEach((heading) => observer.observe(heading))
    }
  })
}

let initialized = false
function initializeApplication() {
  mountNavigation()
  mountVueIslands()
  initializeLegacyUtilities()
  initializeTabs()
  initializeResponsiveTables()
  initializeIepNavigation()
  initializeDateTimeRanges()
  if (!initialized) {
    initializeDialogs()
    initialized = true
  }
}

document.addEventListener("DOMContentLoaded", initializeApplication)
document.addEventListener("turbo:load", initializeApplication)

import "./students.js"
import "./counsels.js"
import "./manage.js"
import "./meetings.js"
import "./school-settings.js"
import "./iep-document-editor.js"
import "./passkeys.js"
